// A third-party service's mark (D-040), shown next to its name: Google, Meta, WhatsApp, eSewa…
// Logo marks render in the brand's colour; providers without a published mark get a colour monogram.
// Decorative: the visible name next to it is the label.
import { BRAND_MARKS, type BrandMark, type BrandName } from "../third-party/brands";

export type { BrandName };

export function BrandIcon({
  brand,
  size = 20,
  className,
  mono = false,
  active = false,
}: {
  brand: BrandName;
  size?: number;
  className?: string;
  /** The mark's shape in the text colour, like our own icons (the sidebar; owner comment c8). */
  mono?: boolean;
  /** With `mono`: paint the shape with the brand gradient, like an active icon. */
  active?: boolean;
}) {
  const mark: BrandMark = BRAND_MARKS[brand];
  if (mono && mark.mark) {
    // The mark as a mask over the current colour; hover and active take the brand gradient
    // (`.ady-brand-mono` in components.css).
    const src = `url("${mark.mark}")`;
    return (
      <span
        aria-hidden="true"
        className={className ? `ady-brand-mono ${className}` : "ady-brand-mono"}
        data-active={active ? "" : undefined}
        style={{ width: size, height: size, WebkitMaskImage: src, maskImage: src }}
      />
    );
  }
  if (mark.mark) {
    // A square symbol-only vector made for small spots (courier marks).
    return (
      <img
        src={mark.mark}
        alt=""
        aria-hidden="true"
        className={className}
        width={size}
        height={size}
        style={{ flex: "none", borderRadius: Math.round(size / 5) }}
      />
    );
  }
  if (mark.logo && mark.symbol !== false) {
    // The company's own logo, cropped to its symbol (every courier logo starts with it on the left).
    return (
      <span
        aria-hidden="true"
        className={className}
        style={{
          display: "inline-flex",
          flex: "none",
          width: size,
          height: size,
          overflow: "hidden",
          borderRadius: Math.round(size / 5),
          background: mark.logoBg,
        }}
      >
        <img src={mark.logo} alt="" style={{ height: size, width: "auto", maxWidth: "none" }} />
      </span>
    );
  }
  if (brand === "google") {
    // Google's sign-in guidelines use the four-colour "G".
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className={className}
      >
        <path
          fill="#4285F4"
          d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
        />
        <path
          fill="#34A853"
          d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
        />
        <path
          fill="#FBBC05"
          d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
        />
        <path
          fill="#EA4335"
          d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"
        />
      </svg>
    );
  }
  if (mark.monogram) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        className={className}
      >
        <rect width="24" height="24" rx="6" fill={mark.color ?? "currentColor"} />
        <text
          x="12"
          y="16.5"
          textAnchor="middle"
          fontSize="13"
          fontWeight="700"
          fill="white"
          fontFamily="inherit"
        >
          {mark.monogram}
        </text>
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path d={mark.path} fill={mark.color ?? "currentColor"} />
    </svg>
  );
}

/** The full logo (symbol and name) on the background it was made for; a mark for brands without one. */
export function BrandLogo({
  brand,
  height = 20,
  className,
}: {
  brand: BrandName;
  height?: number;
  className?: string;
}) {
  const mark: BrandMark = BRAND_MARKS[brand];
  if (!mark.logo) {
    return (
      <span className={className} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <BrandIcon brand={brand} size={height + 6} />
        <span style={{ fontWeight: 600 }}>{mark.title}</span>
      </span>
    );
  }
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        flex: "none",
        alignItems: "center",
        padding: "4px 8px",
        borderRadius: 6,
        background: mark.logoBg,
        boxShadow: "inset 0 0 0 1px rgb(0 0 0 / 0.08)",
      }}
    >
      <img src={mark.logo} alt={mark.title} style={{ height, width: "auto" }} />
    </span>
  );
}

/** Finds the mark for a provider key or name as it appears in data ("esewa", "Khalti", "ga4"…). */
export function brandFor(key: string | null | undefined): BrandName | null {
  if (!key) return null;
  const k = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  const alias: Record<string, BrandName> = {
    ga4: "googleanalytics",
    googleanalytics: "googleanalytics",
    gtm: "googletagmanager",
    googletagmanager: "googletagmanager",
    google: "google",
    metapixel: "meta",
    meta: "meta",
    facebook: "facebook",
    instagram: "instagram",
    tiktok: "tiktok",
    tiktokpixel: "tiktok",
    whatsapp: "whatsapp",
    messenger: "messenger",
    clarity: "clarity",
    microsoftclarity: "clarity",
    tawk: "tawk",
    tawkto: "tawk",
    esewa: "esewa",
    khalti: "khalti",
    fonepay: "fonepay",
    fonepayqr: "fonepay",
    fonepayintent: "fonepay",
    fonepayweb: "fonepay",
    stripe: "stripe",
    pathao: "pathao",
    ncm: "ncm",
    nepalcanmove: "ncm",
    pathaoparcel: "pathao",
    pickndrop: "pickndrop",
    pickanddrop: "pickndrop",
    pnd: "pickndrop",
    upaya: "upaya",
    helloservice: "helloservice",
    helloservicenepal: "helloservice",
    daraz: "daraz",
    darazexpress: "daraz",
    dex: "daraz",
    upayacitycargo: "upaya",
  };
  return alias[k] ?? null;
}
